import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import LogoSpinner from '@/components/ui/LogoSpinner'
import { hasRequiredMemberName } from '@/utils/memberName'

interface ProtectedRouteProps {
  children: ReactNode
  /** Restrict to a specific role (admin areas) */
  requireRole?: 'ADMIN' | 'BOARD'
}

/**
 * Gates member-only surfaces. Anonymous users get bounced to /login with
 * `returnTo` set so we can restore intent after sign-in. Allowlist denials
 * route to /access-denied.
 */
const ProtectedRoute = ({ children, requireRole }: ProtectedRouteProps) => {
  const { status, user, isLoading, supabaseUser } = useAuthStore()
  const location = useLocation()

  if (isLoading || status === 'unknown') {
    return <FullPageLoader />
  }

  if (status === 'denied') {
    return <Navigate to="/access-denied" replace />
  }

  if (status === 'anonymous') {
    return (
      <Navigate
        to="/login"
        replace
        state={{ returnTo: location.pathname + location.search }}
      />
    )
  }

  // Authenticated but profile hasn't loaded yet — keep the loader up.
  if (!user) return <FullPageLoader />

  // First-time members MUST create a password before using the portal. Any
  // member-only route bounces to /set-password until passwordSet is true.
  // (/set-password lives outside ProtectedRoute, so there's no redirect loop.)
  const passwordSet = supabaseUser?.user_metadata?.['passwordSet'] === true
  if (!passwordSet) {
    return <Navigate to="/set-password" replace />
  }

  if (!hasRequiredMemberName(user)) {
    return (
      <Navigate
        to="/complete-profile"
        replace
        state={{ returnTo: location.pathname + location.search }}
      />
    )
  }

  if (requireRole && user.role !== requireRole && user.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

const FullPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center paper-bg">
    <LogoSpinner size="xl" label="Shorewood" />
  </div>
)

export default ProtectedRoute
